<template>
  <div class="unit-container" v-bind:class="{ required: mandatory }">
    <!--
      Handle the case if there are any non unit relationships (major, co-major, minor)
      and or condition sets
     -->

    <div v-if="credits > 0" class="unit-container__head">
      <div v-if="credits > 0" class="unit-container__head-title">{{container.title}}</div>
      <div v-if="credits > 0 && credits != maxCredits" class="unit-container__head-error">
        {{ credits }} points selected
      </div>
    </div>

    <div class="page-alert warning mb-2" v-if="reviewMode &&  missingUnitsFromSavedPlan.length > 0 && relationships.length > 0 && units.length > 0">
      <div class="page-alert__icon"><i class="far fa-exclamation-circle"></i></div>
      <div class="page-alert__body">
        <p>The following selection{{missingUnitsFromSavedPlan.length === 1 ? ' is' : 's are'}} no longer available.</p>
        <ul class="mb-0">
          <li v-for="error in missingUnitsFromSavedPlan" :key="error">
            <strong>{{getMissingUnitId(error)}}</strong>
          </li>
        </ul>
      </div>
    </div>

    <div class="unit-container__description" v-if="container.description.length && credits > 0">{{ container.description }}</div>

    <template v-if="canAdd && containerHasSelection">
      <div class="unit-group__add-edit-actions">

        <button type="button" class="btn-spot-text" v-on:click="openSlideOut({component: slideOutComponent, componentProperties: slideOutComponentProperties})">
          <i :class="['far icon-spot primary', credits > 0 ? 'fa-pencil' :'fa-plus']"></i>
          {{ credits > 0 ? 'Edit' : 'Add' }} {{ container.title }}
          <!-- <small>({{this.container.credits}} points)</small> -->
        </button>

      </div>
    </template>

    <!-- Handle top level relationships -->
    <template v-if="units && units.length">
      <div class="unit-card__row">
        <UnitCard v-for="(item,index) in selectedItemsByLocation(location)"
          :key="item.relationship.code+'-'+ item.relationship.order+'-'+index"
          :location="item.location" :unit="item.relationship"
          :firstLevelTitle="firstLevelContainerTitle"
          :canDelete="container.connector === 'OR' || item.relationship.connector === 'OR'" />
      </div>
    </template>

    <!-- Handle containers -->
    <template v-if="containers && containers.length">
      <UnitSubGroup v-for="(con,index) in containers" :key="index" :firstLevelContainer="firstLevelContainer"
        :container="con" :location="getContainerLocation(con)" />
    </template>

    <!-- Handle non unit relationships. Most of the time this will be major. -->
    <template v-if="nonUnitRelationships && nonUnitRelationships.length">
      <template>
        <UnitSubGroup v-for="(rel,index) in populatedNonUnitRelationships"
          :key="index" :parentLocation="location"
          :relationship="rel" :location="getRelationshipLocation(rel)"
          :virtualRelationship="rel.virtual" :firstLevelContainer="firstLevelContainer" />
      </template>
    </template>

  </div>
</template>

<script>
import { dynamicSort } from 'common/helpers/dynamic-sort.js'
import SelectionLocationHelper from 'client/js/utils/selection-location-helper.js'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { onlyUnique } from 'common/helpers/only-unique'

import UnitSubGroup from './UnitSubGroup.vue'
import UnitCard from './UnitCard.vue'
import SlideOutTreeView from './slide-out-tree-view/SlideOutTreeView.vue'

export default {
  props: {
    container: {
      type: Object,
      required: true
    },
    location: {
      type: String,
      required: true
    },
    autoSelectMandatory: {
      type: Boolean,
      required: false,
      default: false
    },
    canAdd: {
      type: Boolean,
      required: false,
      default: false
    },
    showContainerOption: {
      type: Boolean,
      required: false,
      default: false
    },
    firstLevelContainer: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      slideOutComponent: SlideOutTreeView,
      slideOutComponentProperties: {
        container: this.container,
        location: this.location,
        mandatory: this.container && this.container.connector === 'AND',
        firstLevelContainer: this.firstLevelContainer
      }
    }
  },
  methods: {
    ...mapMutations('global', {
      openSlideOut: 'openSlideOut'
    }),
    ...mapActions({
      attachStructure: 'courses/attachStructure',
      addRelationships: 'courses/addRelationships',

      addItem: 'units/addCurriculumItem'
    }),
    unitIsOptional(relationship) {
      return relationship.connector === 'OR' && relationship.type === 'Unit'
    },
    unitIsMandatory(relationship) {
      return relationship.connector === 'AND' && relationship.type === 'Unit'
    },
    getContainerLocation(container, prefix = '') {
      return SelectionLocationHelper.getContainerLocation(this.location, container)
    },
    getRelationshipLocation(relationship) {
      return SelectionLocationHelper.getRelationshipLocation(this.location, relationship)
    },
    getMissingUnitId(unit) {
      let split = unit.split('>')
      return split[split.length - 1].split('v')[0]
    }
  },
  computed: {
    ...mapGetters({
      savedPlan: 'savedCourse/getSavedCoursePlan',
      selectedItemsByLocation: 'units/getSelectedCurriculumItemByLocation',
      totalCredits: 'units/getTotalCredits',
      savedMissingUnits: 'savedCourse/getMissingUnits'
    }),
    missingUnitsFromSavedPlan: function () {
      return this.savedMissingUnits?.filter((u) => u.indexOf(this.location) > -1).filter(onlyUnique) || []
    },
    reviewMode: function () {
      return this.savedPlan != null
    },
    maxCredits() {
      return parseInt(this.container.credits) || 0
    },
    containers: function () {
      return this.container.container.slice().sort(dynamicSort('order'))
    },
    relationships: function () {
      return this.container.relationship.slice().sort(dynamicSort('order'))
    },
    units: function () {
      return this.relationships.filter((r) => r.type === 'Unit')
    },
    virtualUnits: function () {
      return this.units.filter((u) => u.virtual)
    },
    mandatoryUnits: function () {
      return this.units.filter((x) => x.connector === 'AND')
    },
    optionalUnits: function () {
      return this.units.filter((x) => x.connector === 'OR')
    },
    /**
     * returns other relationships than units (i.e. Major, Co-Major, or Minor)
     */
    nonUnitRelationships: function () {
      return this.relationships.filter((r) => r.type !== 'Unit')
    },

    mandatoryRelationships: function () {
      return this.nonUnitRelationships.filter((r) => r.connector === 'AND')
    },
    /**
     * returns other relationships than units (i.e. Major, Co-Major, or Minor)
     */
    populatedNonUnitRelationships: function () {
      return this.nonUnitRelationships.filter((rel) => rel.structure && rel.structure.containers.length)
    },
    conditionSetContainers: function () {
      return this.container.conditionSet
    },
    /**
     * return true if either this container or any of the children containers contains vertical grouping
     */
    containersHasVerticalGrouping() {
      return this.container.verticalGrouping || this.containers.filter((c) => c.verticalGrouping !== null).length
    },
    hasEnoughCredits() {
      return this.credits != 0 && this.credits >= this.maxCredits
    },
    selectedItems() {
      return this.selectedItemsByLocation(this.location)
    },
    firstLevelContainerLocation() {
      return SelectionLocationHelper.getContainerLocation('root', this.firstLevelContainer)
    },
    firstLevelContainerTitle() {
      return this.firstLevelContainer ? this.firstLevelContainer.title : ''
    },
    credits() {
      return this.totalCredits(this.location, true)
    },
    containerHasSelection() {
      return (
        (this.container.relationship && this.container.relationship.filter((rel) => rel.connector === 'OR').length) ||
        (this.container.container && this.container.container.length) ||
        (this.container.conditionSet && this.container.conditionSet.length) ||
        this.container.connector === 'OR' ||
        this.mandatoryRelationships.length > 0
      )
    },
    mandatory() {
      return this.container && this.container.connector === 'AND'
    }
  },
  mounted() {
    // automatically add required unit
    if (this.autoSelectMandatory && this.mandatoryUnits && this.mandatoryUnits.length) {
      this.mandatoryUnits.forEach((rel) => {
        const relLocation = this.getRelationshipLocation(rel)
        const selected = this.selectedItems.filter((item) => item.id === rel.id && item.location === relLocation).length > 0
        if (!selected) {
          this.addItem({
            firstLevelTitle: this?.firstLevelContainerTitle,
            relationship: rel,
            location: relLocation
          })
        }
      })
    }
  },
  beforeDestroy() {},
  components: { UnitSubGroup, UnitCard, SlideOutTreeView }
}
</script>
